import { billingClient } from '../lib/client';
import {
    BillingReportParams,
    FileDownloadResponse,
    FileType,
    BillingAnalyticsParams,
    BillingTrendsParams,
    ApiResponse,
    BillingOverview,
    BillingTrends,
    UsagePatterns,
    MessagingOverview
} from './billing.types';
import { BILLING_ENDPOINTS, validateDateRange, validateMonthsParam } from './billing.utils';

/**
 * Get billing overview with summary metrics, top assistants, and call status breakdown
 * @param params Optional date range filters
 * @returns Billing overview data
 */
export const getBillingOverview = async (params?: BillingAnalyticsParams): Promise<ApiResponse<BillingOverview> | { error: string }> => {
  try {
    console.log('Getting billing overview for:', params);
    
    // Validate date range if provided
    if (params?.startDate || params?.endDate) {
      const validation = validateDateRange(params.startDate, params.endDate);
      if (!validation.isValid) {
        return { error: validation.error || 'Invalid date range' };
      }
    }
    
    const response = await billingClient.get(BILLING_ENDPOINTS.ANALYTICS_OVERVIEW, {
      params: {
        ...(params?.startDate && { startDate: params.startDate }),
        ...(params?.endDate && { endDate: params.endDate })
      }
    });
    
    return response.data;
  } catch (error: unknown) {
    console.error('Billing overview error:', error);
    const errorMessage = (error as Error).message || 'Failed to get billing overview';
    return { error: errorMessage };
  }
};

/**
 * Get monthly billing trends with growth analysis
 * @param params Optional months parameter (1-24, default 6)
 * @returns Billing trends data
 */
export const getBillingTrends = async (params?: BillingTrendsParams): Promise<ApiResponse<BillingTrends> | { error: string }> => {
  try {
    console.log('Getting billing trends for:', params);
    
    // Validate months parameter if provided
    if (params?.months !== undefined) {
      const validation = validateMonthsParam(params.months);
      if (!validation.isValid) {
        return { error: validation.error || 'Invalid months parameter' };
      }
    }
    
    const response = await billingClient.get(BILLING_ENDPOINTS.ANALYTICS_TRENDS, {
      params: {
        ...(params?.months && { months: params.months })
      }
    });
    
    return response.data;
  } catch (error: unknown) {
    console.error('Billing trends error:', error);
    const errorMessage = (error as Error).message || 'Failed to get billing trends';
    return { error: errorMessage };
  }
};

/**
 * Get usage patterns by hour and day with peak times identification
 * @param params Optional date range filters
 * @returns Usage patterns data
 */
export const getUsagePatterns = async (params?: BillingAnalyticsParams): Promise<ApiResponse<UsagePatterns> | { error: string }> => {
  try {
    console.log('Getting usage patterns for:', params);
    
    // Validate date range if provided
    if (params?.startDate || params?.endDate) {
      const validation = validateDateRange(params.startDate, params.endDate);
      if (!validation.isValid) {
        return { error: validation.error || 'Invalid date range' };
      }
    }
    
    const response = await billingClient.get(BILLING_ENDPOINTS.ANALYTICS_PATTERNS, {
      params: {
        ...(params?.startDate && { startDate: params.startDate }),
        ...(params?.endDate && { endDate: params.endDate })
      }
    });
    
    return response.data;
  } catch (error: unknown) {
    console.error('Usage patterns error:', error);
    const errorMessage = (error as Error).message || 'Failed to get usage patterns';
    return { error: errorMessage };
  }
};

/**
 * Get messaging overview with session breakdown per assistant
 * @param params Optional date range filters
 * @returns Messaging overview data
 */
export const getMessagingOverview = async (params?: BillingAnalyticsParams): Promise<ApiResponse<MessagingOverview> | { error: string }> => {
  try {
    if (params?.startDate || params?.endDate) {
      const validation = validateDateRange(params.startDate, params.endDate);
      if (!validation.isValid) {
        return { error: validation.error || 'Invalid date range' };
      }
    }

    const response = await billingClient.get(BILLING_ENDPOINTS.MESSAGING_OVERVIEW, {
      params: {
        ...(params?.startDate && { startDate: params.startDate }),
        ...(params?.endDate && { endDate: params.endDate })
      }
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Messaging overview error:', error);
    const errorMessage = (error as Error).message || 'Failed to get messaging overview';
    return { error: errorMessage };
  }
};

/**
 * Downloads a billing report as an Excel file or ZIP archive for the specified date range
 * This function can take a long time for extensive date ranges
 * @returns URL to download the file and the file type, and the filename from the server
 */
export const downloadBillingReport = async (params?: BillingReportParams): Promise<FileDownloadResponse> => {
  try {
    console.log('Downloading billing report for:', params);
    
    // Validate date range if provided
    if (params?.startDate || params?.endDate) {
      const validation = validateDateRange(params.startDate, params.endDate);
      if (!validation.isValid) {
        return { error: validation.error || 'Invalid date range' };
      }
    }
    
    // Using a client with no timeout for potentially long-running report generation
    const response = await billingClient.get(BILLING_ENDPOINTS.GENERATE_REPORT, {
      params: {
        ...(params?.startDate && { startDate: params.startDate }),
        ...(params?.endDate && { endDate: params.endDate })
      },
      responseType: 'arraybuffer',  // Specify as arraybuffer for this request only
      transformResponse: [(data) => data]  // Prevent any data transformation
    });
    
    console.log('Response received:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      dataType: typeof response.data,
      dataLength: response.data?.byteLength || 'unknown'
    });
    
    // Check for empty response
    if (!response.data || response.data.byteLength === 0) {
      console.error('Empty response data received');
      return { error: 'No data received from server' };
    }
    
    // Determine file type from Content-Type header
    const contentType = response.headers['content-type'];
    console.log('Content-Type header:', contentType);
    let fileType: FileType = 'xlsx';
    
    if (contentType?.includes('application/zip') || contentType?.includes('x-zip-compressed')) {
      fileType = 'zip';
    } else if (contentType?.includes('spreadsheetml.sheet')) {
      fileType = 'xlsx';
    }
    console.log('Determined file type:', fileType);
    
    // Try to get filename from Content-Disposition header
    let filename = '';
    const contentDisposition = response.headers['content-disposition'];
    console.log('Content-Disposition header:', contentDisposition);
    
    if (contentDisposition) {
      // Try to extract filename from Content-Disposition header
      const match = contentDisposition.match(/filename=["]?([^";\n]*)["']?/i);
      if (match && match[1]) {
        filename = match[1].trim();
        console.log('Extracted filename:', filename);
      }
    }
    
    // If no filename from header, create one based on dates
    if (!filename) {
      const startDate = params?.startDate || 'no-start';
      const endDate = params?.endDate || 'no-end';
      filename = `billing-report-${startDate}-to-${endDate}.${fileType}`;
      console.log('Using generated filename:', filename);
    }
    
    try {
      // Set the correct MIME type for the file
      const mimeType = fileType === 'xlsx' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        : 'application/zip';
      
      console.log('Creating blob with MIME type:', contentType || mimeType);
      
      // Create blob directly from the arraybuffer
      const blob = new Blob([response.data], { 
        type: contentType || mimeType 
      });
      
      console.log('Blob created successfully:', {
        size: blob.size,
        type: blob.type
      });
      
      // Create object URL
      const url = URL.createObjectURL(blob);
      console.log('URL created successfully:', url);
      
      return { url, fileType, filename };
    } catch (blobError) {
      console.error('Error creating blob or URL:', blobError);
      return { error: `Error processing file: ${(blobError as Error).message}` };
    }
  } catch (error: unknown) {
    console.error('Billing report download error:', error);
    
    // Handle timeout errors specifically
    if ((error as { code?: string }).code === 'ECONNABORTED') {
      return { error: 'The request took too long to complete. Please try a smaller date range.' };
    }
    
    const errorDetail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || '';
    const errorMessage = errorDetail
      ? errorDetail
          .replace(/status_code: \d+, body: /, '')
          .replace(/[{}'"]/g, '')
          .replace(/message: /, '')
          .replace(/, error: .*$/, '')
          .trim()
      : (error as Error).message || 'Failed to download billing report';
      
    return { error: errorMessage };
  }
};
